import React, { useState } from 'react';
import { Button, Grid, CircularProgress, Alert, Collapse, IconButton } from '@mui/material';
import { send_request } from '../../../../../utils/Request';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AfterRunAlert from '../AfterRunAlert';
import { AFTER_RUN_ALERT_TYPE } from '../ForceStartConstants';

import './TriggerFromApiPollingTestInfo.css';

const TriggerFromApiPollingTestInfo = ({
  componentId,
  projectId,
  version,
  TriggerTitle,
  TriggerDescription,
  isTest,
  save,
  disabled,
}) => {
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [responseExpanded, setResponseExpanded] = useState(true);
  const [showAfterRunAlert, setShowAfterRunAlert] = useState(null);
  const [successSessionId, setSuccessSessionId] = useState(null);
  const [triggeredProjectId, setTriggeredProjectId] = useState(null);

  const executeTestPoll = async () => {
    setLoading(true);
    setError(null);
    setTestResult(null);
    setShowAfterRunAlert(null);
    setSuccessSessionId(null);
    setTriggeredProjectId(null);

    try {
      // Save the component first to ensure latest config is used
      if (save) {
        await save();
      }

      // Step 1: Execute the test poll
      const pollResponse = await send_request(
        `component-service/trigger_from_api_polling/execute-test-poll/${componentId}?projectId=${projectId}`,
        {},
        {},
        'POST'
      );

      if (pollResponse && pollResponse.data) {
        setTestResult(pollResponse.data);

        // Step 2: Automatically trigger the test session with the poll result
        const triggerResponse = await send_request(
          `component-service/trigger_from_api_polling/trigger-test-session/${componentId}`,
          pollResponse.data,
          {},
          'POST'
        );

        if (triggerResponse && triggerResponse.data && triggerResponse.data.success) {
          setSuccessSessionId([triggerResponse.data.sessionId]);
          setTriggeredProjectId(triggerResponse.data.projectId);
          setShowAfterRunAlert(AFTER_RUN_ALERT_TYPE.SUCCESS);
        } else {
          setError(triggerResponse?.data?.error || 'Failed to trigger test session');
          setShowAfterRunAlert(AFTER_RUN_ALERT_TYPE.ERROR);
        }
      } else {
        setError('No response received from test poll');
        setShowAfterRunAlert(AFTER_RUN_ALERT_TYPE.ERROR);
      }
    } catch (e) {
      console.error('Test poll error:', e);
      setError(e.message || 'Failed to execute test poll');
      setShowAfterRunAlert(AFTER_RUN_ALERT_TYPE.ERROR);
    }

    setLoading(false);
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
  };

  const formatResponseBody = body => {
    if (!body) return '';
    try {
      const parsed = typeof body === 'string' ? JSON.parse(body) : body;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof body === 'string' ? body : JSON.stringify(body);
    }
  };

  const getStatusColor = statusCode => {
    if (!statusCode) return '#666';
    if (statusCode >= 200 && statusCode < 300) return '#2e7d32';
    if (statusCode >= 400) return '#d32f2f';
    return '#f57c00';
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TriggerTitle>Test API Polling Trigger</TriggerTitle>
      </Grid>
      <Grid item xs={12}>
        <TriggerDescription>
          Perform a real HTTP request to the configured API endpoint. The response will be used to
          trigger a test session automatically.
        </TriggerDescription>
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={executeTestPoll}
          disabled={loading || disabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
        >
          {loading ? 'Running Test...' : 'Run Test'}
        </Button>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      {testResult && (
        <Grid item xs={12}>
          <div className="api-polling-test-result">
            <div className="api-polling-test-result-header">
              <div className="api-polling-test-result-status">
                {testResult.matchedSuccessCriteria ? (
                  <CheckCircleIcon className="status-icon success" />
                ) : (
                  <ErrorIcon className="status-icon error" />
                )}
                <span
                  className="status-code"
                  style={{ color: getStatusColor(testResult.statusCode) }}
                >
                  {testResult.statusCode ? `Status ${testResult.statusCode}` : 'No Response'}
                </span>
                <span className="status-match">
                  {testResult.matchedSuccessCriteria
                    ? '(Matches success criteria)'
                    : '(Does not match success criteria)'}
                </span>
              </div>
              <div className="api-polling-test-result-info">
                <span className="info-label">Success codes:</span>
                <span className="info-value">{testResult.configuredSuccessCodes || 'N/A'}</span>
              </div>
            </div>

            {testResult.error && (
              <Alert severity="warning" className="api-polling-test-error">
                {testResult.error}
              </Alert>
            )}

            {testResult.responseBody && (
              <div className="api-polling-test-response-section">
                <div
                  className="api-polling-test-response-header"
                  onClick={() => setResponseExpanded(!responseExpanded)}
                >
                  <span className="response-label">Response Body</span>
                  <div className="response-actions">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        copyToClipboard(testResult.responseBody);
                      }}
                      title="Copy response"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      {responseExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </div>
                </div>
                <Collapse in={responseExpanded}>
                  <pre className="api-polling-test-response-body">
                    {formatResponseBody(testResult.responseBody)}
                  </pre>
                </Collapse>
              </div>
            )}

            {showAfterRunAlert && (
              <div style={{ marginTop: '16px' }}>
                <AfterRunAlert
                  alertType={showAfterRunAlert}
                  sessionId={successSessionId}
                  projectId={triggeredProjectId}
                  isLoading={loading}
                  isTest={true}
                />
              </div>
            )}
          </div>
        </Grid>
      )}
    </Grid>
  );
};

export default TriggerFromApiPollingTestInfo;
